import { useEffect } from 'react';
import { isNative } from '../api/origin.ts';

/**
 * The on-screen keyboard, inside the shell only.
 *
 * WKWebView answers a focused input by scrolling its own scroll view until the
 * input clears the keyboard. On a layout that is one `height: 100%` column with
 * nothing pinned in it, that slides the whole app upwards — the conversation
 * header leaves the screen on focus and comes back on blur, which is most of
 * what «the UI keeps moving» meant on the phone. Android never did it: the
 * `interactive-widget=resizes-content` in index.html shortens the layout
 * instead, and every screen here is built for that shape.
 *
 * Two thirds of the fix are declarations rather than code and live in
 * capacitor.config.ts: `Keyboard.resize: native` shrinks the web view around
 * the keyboard, `ios.scrollEnabled: false` takes away the scroll view the pan
 * needed. The plugin also hides the `^ v ✓` accessory bar as it loads, which is
 * the grey strip that used to sit above the keyboard. What is left for this
 * file is the part only the web side knows.
 */

/** What `@capacitor/keyboard`'s `addListener` hands back. */
type Handle = { remove: () => Promise<void> };

/**
 * The keyboard's height while it is up, and null while it is down.
 *
 * Reported by the OS rather than measured, which matters now that the web view
 * resizes: the gap between the layout and visual viewports that
 * `useKeyboardInset` reads on the web closes to nothing here, because with the
 * native resize there is no longer a page sticking out under the keyboard to
 * measure. The number also includes the home indicator and the accessory bar,
 * so it is the height something taking the keyboard's place has to match.
 *
 * Off the phone it never calls back and the returned cleanup does nothing.
 */
export function onKeyboardHeight(report: (height: number | null) => void): () => void {
  if (!isNative) return () => {};

  // Loaded on demand so the web bundle never fetches the plugin, the same way
  // useNativeBack loads @capacitor/app: `isNative` is decided at build time,
  // but the bundler cannot see that through the module boundary.
  let disposed = false;
  const handles: Handle[] = [];
  const drop = () => {
    for (const handle of handles.splice(0)) void handle.remove();
  };
  void import('@capacitor/keyboard')
    .then(async ({ Keyboard }) => {
      handles.push(
        await Keyboard.addListener('keyboardWillShow', (info) => report(info.keyboardHeight)),
        await Keyboard.addListener('keyboardWillHide', () => report(null)),
      );
      if (disposed) drop();
    })
    // A plugin that is not there is a remembered height instead of a measured
    // one, which is what this hook falls back to anyway. It is not worth an
    // unhandled rejection — and it is reachable: a native-mode bundle opened in
    // a browser, which is how the native client is tested without a phone.
    .catch(() => {});

  return () => {
    disposed = true;
    drop();
  };
}

/**
 * Zeroes the bottom safe-area inset while the keyboard is up, on iOS.
 *
 * Once the web view is resized it no longer reaches the bottom of the screen —
 * the keyboard covers the home indicator, not the app — so a composer that
 * still paid `--safe-bottom` would leave a second gap under a keyboard that
 * had already cleared it. tokens.css describes this correction for Android,
 * where Capacitor makes it itself; on iOS the inset comes from `env()` and
 * there is nobody to make it but us.
 *
 * Hence the platform gate rather than a plain `isNative`: on Android that
 * variable belongs to Capacitor, and writing to it would be taking it away.
 */
export function useNativeKeyboard(): void {
  useEffect(() => {
    if (!isNative) return;
    const platform = (
      window as unknown as { Capacitor?: { getPlatform?: () => string } }
    ).Capacitor?.getPlatform?.();
    if (platform !== 'ios') return;

    const root = document.documentElement;
    const stop = onKeyboardHeight((height) => {
      if (height === null) root.style.removeProperty('--safe-area-inset-bottom');
      else root.style.setProperty('--safe-area-inset-bottom', '0px');
    });
    return () => {
      stop();
      root.style.removeProperty('--safe-area-inset-bottom');
    };
  }, []);
}
